import { graphql } from 'gatsby';

import React from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { useHasEnteredViewport } from '../../../utils/hooks';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import ScrollingItems from '../ScrollingItems';
import SmartLink from '../SmartLink';
import * as styles from './FeaturedInModule.module.scss';

const ELEMENT_ANIMATION_DURATION = 10;

export const FeaturedInModuleFragment = graphql`
  fragment FeaturedInModule on SanityFeaturedInModule {
    title {
      ...LocaleStringFragment
    }
    featuredItems {
      quote {
        ...LocaleStringFragment
      }
      logo {
        lightLogo {
          ...SanityImage
        }
        darkLogo {
          ...SanityImage
        }
      }
      link
    }
  }
`;

interface FeaturedItem {
  quote: string;
  logo: {
    lightLogo: SanityImageType;
    darkLogo: SanityImageType;
  };
  link?: string;
}

export type FeaturedInModuleProps = {
  title?: string;
  featuredItems: Array<FeaturedItem>;
  className?: string;
};

function FeaturedInModule(props: FeaturedInModuleProps & CommonModuleProps): React.ReactElement {
  const { title, featuredItems, className, moduleId } = props;
  const [viewportRef, hasEnteredViewport] = useHasEnteredViewport();

  function insertBreakInMiddleIfShort(str: string, maxLength: number): React.ReactNode {
    if (str.length <= maxLength) {
      const middle = Math.floor(str.length / 2);
      const leftSpaceIndex = str.lastIndexOf(' ', middle);
      const rightSpaceIndex = str.indexOf(' ', middle);
      const breakIndex = leftSpaceIndex !== -1 ? leftSpaceIndex : rightSpaceIndex;

      if (breakIndex !== -1) {
        return (
          <>
            {str.slice(0, breakIndex)}
            <br />
            {str.slice(breakIndex + 1)}
          </>
        );
      }
    }
    return <>{str}</>;
  }

  function renderFeaturedItem(item: FeaturedItem) {
    const logo =
      props.theme === 'moonlit' || props.theme === 'starlit'
        ? item.logo.lightLogo
        : item.logo.darkLogo;

    // Calculate height for equal visual area across different aspect ratios
    // Formula: height = baseHeight / sqrt(aspectRatio)
    const BASE_HEIGHT = 60;
    const MIN_HEIGHT = 30;
    const MAX_HEIGHT = 70;
    const aspectRatio = logo.asset.metadata?.dimensions?.aspectRatio || 1;
    const calculatedHeight = Math.round(BASE_HEIGHT / Math.sqrt(aspectRatio));
    const logoHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, calculatedHeight));

    return (
      <div className={styles.itemContainer}>
        <blockquote className={styles.quote}>
          "{insertBreakInMiddleIfShort(item.quote, 50)}"
        </blockquote>
        <div className={styles.divider}></div>
        <div className={styles.logoContainer} style={{ height: logoHeight }}>
          <Image
            className={styles.logo}
            image={logo}
            width={200}
            eagerLoading={hasEnteredViewport}
          />
        </div>
      </div>
    );
  }

  return (
    <ModuleLayout
      id={moduleId}
      ref={viewportRef}
      className={className}
      childrenClassName={styles.contentContainer}
      currentModuleBgColor={props.theme}
      childrenWithoutHorizontalPadding
    >
      {title && (
        <div className={styles.titleContainer}>
          <AnimatedTitle title={title} trigger={styles.contentContainer} className={styles.title} />
        </div>
      )}
      <ScrollingItems
        animationDuration={ELEMENT_ANIMATION_DURATION * featuredItems.length + 1}
        className={styles.scrollingWrapper}
      >
        <div className={styles.itemsContainer}>
          {featuredItems.map((item, i) => (
            <React.Fragment key={i}>
              {item.link ? (
                <SmartLink to={item.link} className={styles.link}>
                  {renderFeaturedItem(item)}
                </SmartLink>
              ) : (
                renderFeaturedItem(item)
              )}
            </React.Fragment>
          ))}
        </div>
      </ScrollingItems>
    </ModuleLayout>
  );
}

export default FeaturedInModule;
